import { NgIf } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  Renderer2,
  ViewChild,
  ViewContainerRef,
} from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import {
  FormCheckboxComponent,
  FormCheckboxDirective,
  FormErrorComponent,
  FormInputComponent,
  FormInputDirective,
  FormLabelDirective,
  SvgComponent,
} from '@app/base-components';
import { AbstractContentfulBase } from '@app/contentful';
import {
  ButtonQaId,
  CheckboxQaId,
  HabaQaId,
  mailValidator,
  newsletterUrl,
  TextfieldQaId,
} from '@app/helpers';
import {
  CustomSiteContext,
  NewsletterService,
  ScrollToFirstInvalidControlService,
  UtilsService,
} from '@app/services';
import { Entry } from 'contentful';
import { NewsletterSignupSource } from 'haba-base-components';
import {
  ContentfulDynamicComponentRestService,
  ContentfulRestService,
  ContentfulSeoMetadataService,
  Page,
} from 'haba-contentful-components';
import {
  GlobalMessageService,
  GlobalMessageType,
  I18nModule,
  PageMeta,
  WindowRef,
} from 'haba-core';
import { Subscription } from 'rxjs';
import { finalize, first } from 'rxjs/operators';
import { environment } from 'src/environments/environment';

@Component({
  selector: 'app-newsletter',
  templateUrl: './newsletter.component.html',
  styleUrls: ['./newsletter.component.scss'],
  imports: [
    NgIf,
    ReactiveFormsModule,
    FormInputComponent,
    FormLabelDirective,
    FormInputDirective,
    FormErrorComponent,
    FormCheckboxComponent,
    FormCheckboxDirective,
    SvgComponent,
    I18nModule,
  ],
})
export class NewsletterComponent
  extends AbstractContentfulBase
  implements OnInit, AfterViewInit, OnDestroy
{
  @ViewChild('container', { read: ViewContainerRef }) public target: ViewContainerRef;
  @ViewChild('form') public form: ElementRef;

  public entry: Entry<Page>;
  public formContainer: HTMLElement;

  email: string = '';
  submitted: boolean = false;
  disableSendButton: boolean = false;

  privacyUrl: string = '/s/datenschutz';
  termsUrl: string = '/s/agb';
  cancellationUrl: string = '/s/agb#3-widerrufsbelehrung-fuer-verbraucher';

  baseQaId: HabaQaId;
  emailTextfieldQaId: TextfieldQaId;
  termsCheckboxQaId: CheckboxQaId;
  submitButtonQaId: ButtonQaId;

  clientCredits = {
    accessToken: environment.contentful.token,
    space: environment.contentful.spaceId,
    environment: environment.contentful.environment,
  };

  newsletterForm: FormGroup = this.fb.group({
    isAgreed: [false, Validators.requiredTrue],
    email: ['', [Validators.required, mailValidator()]],
  });

  private subscriptions: Subscription[] = [];

  constructor(
    protected cdr: ChangeDetectorRef,
    protected changeDetectorRef: ChangeDetectorRef,
    protected contentfulDynamicComponentRestService: ContentfulDynamicComponentRestService,
    protected contentfulRestService: ContentfulRestService,
    protected contentfulSeoMetadataService: ContentfulSeoMetadataService,
    protected fb: FormBuilder,
    protected globalMessageService: GlobalMessageService,
    protected newsletterService: NewsletterService,
    protected router: Router,
    protected siteContextService: CustomSiteContext,
    protected winRef: WindowRef,
    private _elementRef: ElementRef,
    private renderer2: Renderer2,
    private scrollToFirstInvalidControlService: ScrollToFirstInvalidControlService,
    private utilsService: UtilsService
  ) {
    super(router, siteContextService);
  }

  ngOnInit(): void {
    this.initIds();
  }

  getLocalizedUrl(url: string): string {
    return this.utilsService.createLocalizedURL(url);
  }

  ngAfterViewInit() {
    this.subscriptions.push(
      this.contentfulRestService
        .getPageBySlug(`${newsletterUrl.subscription}`, this.locale)
        .pipe(first())
        .subscribe((entry: Entry<Page> | null) => {
          if (entry === null) {
            this.notFound();
          } else {
            this.entry = entry;
            const pageMeta: PageMeta = {
              title: this.contentfulSeoMetadataService.getTitle(entry),
              description: this.contentfulSeoMetadataService.getDescription(entry),
              image: this.contentfulSeoMetadataService.getImage(entry),
              robots: this.contentfulSeoMetadataService.getRobots(entry),
              canonicalUrl: this.contentfulSeoMetadataService.getCanonicalUrl(entry, this.winRef),
            };
            this.contentfulSeoMetadataService.setMetadata(pageMeta);
            this.contentfulDynamicComponentRestService.renderComponents(
              entry.fields.content,
              this.target
            );
            this.implementPageComponents();
            this.cdr.detectChanges();
          }
        })
    );
  }

  submitForm() {
    this.scrollToFirstInvalidControlService.scrollToFirstInvalidControl();
    this.submitted = true;
    if (this.newsletterForm.valid) {
      this.sendForm();
    }
  }

  sendForm() {
    this.disableSendButton = true;
    const email = this.newsletterForm.get('email').value;
    this.newsletterService
      .newsletterSubscribe({ email, signupSource: NewsletterSignupSource.NEWSLETTER_PAGE })
      .pipe(finalize(() => (this.disableSendButton = false)))
      .subscribe({
        next: () => {
          this.globalMessageService.add(
            {
              key: 'habaNewsletterProcess.subscription.submitSuccess',
              params: { email },
            },
            GlobalMessageType.MSG_TYPE_CONFIRMATION,
            10000
          );
        },
        error: () => {
          this.globalMessageService.add(
            { key: 'habaNewsletterProcess.subscription.submitError' },
            GlobalMessageType.MSG_TYPE_ERROR
          );
        },
      });
    this.changeDetectorRef.detectChanges();
  }

  ngOnDestroy() {
    this.subscriptions.forEach((sub) => sub.unsubscribe());
  }

  private implementPageComponents(): void {
    const [formContainer] = this._elementRef.nativeElement.querySelectorAll(
      'hcc-internal-logic > div'
    );
    this.formContainer = formContainer;

    this.appendIfParentAvailable('form', formContainer);
  }

  private appendIfParentAvailable(containerTitle: string, parent: HTMLElement): void {
    if (
      parent &&
      this.entry.fields.content.find(
        (container) => container.fields?.internalIdentifier === containerTitle
      )
    ) {
      this.renderer2.appendChild(parent, this[containerTitle].nativeElement);
    }
  }

  private initIds(): void {
    this.baseQaId = HabaQaId.forPage(HabaQaId.page.newsletter);
    this.emailTextfieldQaId = this.baseQaId.getTextfieldId(HabaQaId.elements.textfield.email);
    this.termsCheckboxQaId = this.baseQaId.getCheckboxId(HabaQaId.elements.checkbox.terms);
    this.submitButtonQaId = this.baseQaId.getButtonId(HabaQaId.elements.button.submit);
  }
}
